import React, { useEffect, useState } from "react"
import { Sparkles, AlertTriangle, Check } from "lucide-react"

import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { Switch } from "src/components/ui/switch"
import { useToast } from "src/components/ui/toast"
import { usePairSettings } from "src/features/integrations/hooks/usePairSettings"

// Settings card for the PAIR lead-qualification engine: the kill-switch,
// conversation limits, and the four composite weights. The Anthropic key is
// platform-level (server ENV), so this card only shows its status — it never
// collects a key. Matches the GupshupCard layout so the two sit side-by-side.
export default function PairEngineCard() {
  const { config, isLoading, isSaving, save } = usePairSettings()
  const { toast } = useToast()

  const [form, setForm] = useState({
    enabled: false, max_turns: 6, reminder_gap_hours: 20,
    weight_perception: 0.25, weight_ability: 0.25, weight_intent: 0.3, weight_readiness: 0.2,
  })

  useEffect(() => {
    if (!config) return
    setForm({
      enabled: !!config.enabled,
      max_turns: config.max_turns ?? 6,
      reminder_gap_hours: config.reminder_gap_hours ?? 20,
      weight_perception: config.weight_perception ?? 0.25,
      weight_ability: config.weight_ability ?? 0.25,
      weight_intent: config.weight_intent ?? 0.3,
      weight_readiness: config.weight_readiness ?? 0.2,
    })
  }, [config])

  const setNum = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const weightSum = (
    Number(form.weight_perception) + Number(form.weight_ability) +
    Number(form.weight_intent) + Number(form.weight_readiness)
  ).toFixed(2)

  const handleSave = async () => {
    try {
      const payload = {
        enabled: form.enabled,
        max_turns: Number(form.max_turns),
        reminder_gap_hours: Number(form.reminder_gap_hours),
        weight_perception: Number(form.weight_perception),
        weight_ability: Number(form.weight_ability),
        weight_intent: Number(form.weight_intent),
        weight_readiness: Number(form.weight_readiness),
      }
      await save(payload)
      toast({ title: "PAIR engine settings saved" })
    } catch (e) {
      toast({ variant: "destructive", title: "Couldn't save", description: e?.response?.data?.error || e.message })
    }
  }

  const ready = !!config?.ready
  const aiConfigured = !!config?.ai_configured

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-indigo-500/10 to-violet-600/5 p-6 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-lg hover:shadow-slate-200/50">
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/70 shadow-sm">
          <Sparkles size={24} className="text-indigo-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900">PAIR Qualification Engine</h3>
            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">AI</span>
          </div>
          <span
            className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${ready ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
              }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${ready ? "bg-green-500" : "bg-amber-400"}`} />
            {ready ? "Active" : "Not ready"}
          </span>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            Auto-message every new lead on WhatsApp, score them on PAIR (Perception · Ability · Intent · Readiness),
            and surface the genuine ones on the Qualified Queue. Powered by Claude.
          </p>
        </div>
      </div>

      {!config?.gupshup_ready && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Gupshup (WhatsApp) must be configured first — the engine sends its messages through it.
        </div>
      )}

      <div className="mt-5 space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white/60 px-3 py-2.5">
          <div>
            <Label className="text-sm font-medium text-slate-800">Enable engine</Label>
            <p className="text-[11px] text-slate-400">Auto-qualify new leads as they arrive.</p>
          </div>
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} disabled={isLoading} />
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white/60 px-3 py-2.5">
          <span
            className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-full text-white ${aiConfigured ? "bg-green-500" : "bg-amber-400"
              }`}
          >
            {aiConfigured ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
          </span>
          <div>
            <Label className="text-sm font-medium text-slate-800">AI model access</Label>
            <p className="text-[11px] text-slate-400">
              {aiConfigured
                ? "Connected — runs on the Clozdl platform Anthropic key."
                : "Not configured. Set the ANTHROPIC_API_KEY environment variable on the server."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm">Max questions / lead</Label>
            <Input type="number" min={1} max={20} value={form.max_turns} onChange={setNum("max_turns")} className="mt-1" />
          </div>
          <div>
            <Label className="text-sm">Reminder gap (hrs)</Label>
            <Input type="number" min={1} max={168} value={form.reminder_gap_hours} onChange={setNum("reminder_gap_hours")} className="mt-1" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Composite weights</Label>
            <span className={`text-[11px] font-medium ${weightSum === "1.00" ? "text-slate-400" : "text-amber-600"}`}>
              sum {weightSum} {weightSum !== "1.00" && "(auto-normalized)"}
            </span>
          </div>
          <div className="mt-1 grid grid-cols-4 gap-2">
            {[
              ["weight_perception", "P"],
              ["weight_ability", "A"],
              ["weight_intent", "I"],
              ["weight_readiness", "R"],
            ].map(([key, short]) => (
              <div key={key}>
                <Label className="text-[11px] text-slate-500">{short}</Label>
                <Input type="number" step="0.05" min={0} max={1} value={form[key]} onChange={setNum(key)} className="mt-0.5" />
              </div>
            ))}
          </div>
        </div>

        <Button onClick={handleSave} disabled={isSaving || isLoading} className="w-full">
          {isSaving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  )
}
